import { Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Heart, MessageCircle, Package, Star } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { popPulse, pressable } from "@/lib/motion";
import { useI18n } from "@/providers/i18n-provider";
import { relativeTime, type RelativeTime } from "@famidex/shared";

type Translate = (message: MessageDescriptor | string, values?: Record<string, unknown>) => string;

/** Forma normalizada que consume la tarjeta (REST camelCase o fila PowerSync). */
export interface FeedPost {
  id: string;
  authorName: string | null;
  authorImage: string | null;
  caption: string | null;
  productId: string | null;
  productName: string | null;
  media: string[];
  rating: number | null;
  createdAt: string | null;
}

export function formatRelative(t: Translate, rt: RelativeTime): string {
  const value = rt.value;
  switch (rt.unit) {
    case "week": {
      return t(msg`hace ${value} sem`);
    }
    case "day": {
      return t(msg`hace ${value} d`);
    }
    case "hour": {
      return t(msg`hace ${value} h`);
    }
    case "minute": {
      return t(msg`hace ${value} min`);
    }
    default: {
      return t(msg`ahora`);
    }
  }
}

function initial(name: string | null): string {
  return (name?.trim()[0] ?? "?").toUpperCase();
}

export function PostCard(props: {
  post: FeedPost;
  likeCount: number;
  commentCount: number;
  liked: boolean;
  onToggleLike?: () => void;
  canInteract: boolean;
}) {
  const { t } = useI18n();
  const cover = () => props.post.media[0] ?? null;
  let heartEl: HTMLSpanElement | undefined;

  return (
    <article
      data-testid="post-card"
      class="flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm"
    >
      {/* Autor */}
      <header class="flex items-center gap-3 px-4 pt-4">
        <Show
          when={props.post.authorImage}
          fallback={
            <span class="grid h-10 w-10 place-items-center rounded-full bg-accent text-sm font-bold text-accent-foreground">
              {initial(props.post.authorName)}
            </span>
          }
        >
          <img
            src={props.post.authorImage ?? ""}
            alt=""
            class="h-10 w-10 rounded-full border-2 border-accent object-cover"
          />
        </Show>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-foreground">
            {props.post.authorName ?? t(msg`Coleccionista`)}
          </p>
          <p class="text-xs text-muted-foreground">
            {formatRelative(t, relativeTime(props.post.createdAt, Date.now()))}
          </p>
        </div>
        <Show when={props.post.rating}>
          {(r) => (
            <span class="flex items-center gap-1 text-sm font-semibold text-foreground">
              <Star class="h-4 w-4 fill-warning text-warning" />
              {r()}
            </span>
          )}
        </Show>
      </header>

      {/* Media */}
      <Show when={cover()}>
        <Link to="/community/$postId" params={{ postId: props.post.id }} class="mt-3 block">
          <img
            src={cover() ?? ""}
            alt={props.post.caption ?? ""}
            loading="lazy"
            class="aspect-square w-full bg-muted object-cover"
          />
        </Link>
      </Show>

      {/* Cuerpo */}
      <div class="flex flex-col gap-3 p-4">
        <Show when={props.post.caption}>
          <Link to="/community/$postId" params={{ postId: props.post.id }}>
            <p class="line-clamp-3 text-sm leading-relaxed text-foreground">{props.post.caption}</p>
          </Link>
        </Show>

        <Show when={props.post.productId}>
          {(pid) => (
            <Link
              to="/community/product/$productId"
              params={{ productId: pid() }}
              class="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground"
            >
              <Package class="h-3.5 w-3.5" />
              {props.post.productName ?? t(msg`Ver set`)}
            </Link>
          )}
        </Show>

        {/* Acciones */}
        <div class="flex items-center gap-5 border-t border-border/50 pt-3 text-muted-foreground">
          <button
            type="button"
            data-testid="post-like"
            disabled={!props.canInteract}
            ref={(el: HTMLButtonElement) => pressable(el, { scale: 0.9 })}
            onClick={() => {
              // Like → pop: pulso con resorte del corazón (GPU) + háptica.
              if (heartEl && !props.liked) {
                popPulse(heartEl, { hapticMs: 8 });
              }
              props.onToggleLike?.();
            }}
            class="flex items-center gap-1.5 text-sm transition-colors hover:text-primary disabled:cursor-default disabled:opacity-70"
            classList={{ "text-primary": props.liked }}
            aria-pressed={props.liked}
          >
            <span ref={heartEl} class="grid place-items-center">
              <Heart class="h-5 w-5" classList={{ "fill-primary": props.liked }} />
            </span>
            <span class="tabular-nums">{props.likeCount}</span>
          </button>
          <Link
            to="/community/$postId"
            params={{ postId: props.post.id }}
            class="flex items-center gap-1.5 text-sm transition-colors hover:text-primary"
          >
            <MessageCircle class="h-5 w-5" />
            <span class="tabular-nums">{props.commentCount}</span>
          </Link>
        </div>
      </div>
    </article>
  );
}
